import {getAgronomicFieldObservationStageIds, isThermalOnlyAgronomicProfile} from './copilotModel'
import {normalizeCropId} from '../../../shared/utils/cropCatalog'

const COPY = {
  es: {
    title: 'Referencia de acumulación térmica',
    milestones: 'Hitos observados en el lote',
    milestonesNotice:
      'Son fechas registradas por el usuario, no estadíos predichos ni umbrales térmicos.',
    noMilestones: 'No se registraron hitos observados para este período.',
    notice:
      'Se calculan grados-día como referencia. No se estiman estadíos, madurez ni fechas de cosecha: sus umbrales no están validados para este lote y material.',
    planting: 'Fecha de plantación',
    transplant: 'Fecha de trasplante',
    end: 'Fin de la observación',
    material: 'Variedad o material del lote',
    referenceProfile: 'Perfil de referencia publicado',
    potatoReferenceLabel: 'Papa - referencia térmica Bogotá (2016)',
    tobaccoReferenceLabel: 'Tabaco Virginia K326 - referencia térmica INTA Salta (2014)',
    method: 'Método de cálculo',
    base: 'Temperatura base (°C)',
    source: 'Fuente y alcance de la referencia',
    parameterOrigin: 'Origen de los parámetros',
    publishedParameters: 'Base de la referencia publicada',
    customParameters: 'Base definida por el usuario; no constituye una calibración de estadíos.',
    units: 'Unidad',
    unitValue: '°C·día (GDD)',
    minimumMaximumMean:
      'Media de temperatura mínima y máxima diarias menos la base; acumulación no negativa.',
    dailyMean: 'Temperatura media diaria menos la base; acumulación no negativa.',
    potatoScope:
      'Rodríguez et al. (2016): papa en invernadero en Bogotá, tres variedades colombianas. Es una referencia térmica, no una calibración del material de este lote.',
    tobaccoScope:
      'Diez et al. (2014), INTA Salta: tabaco Virginia K326 en Chicoana, Valle de Lerma. Es una referencia térmica, no una calibración del material de este lote.',
    accumulated: 'GDD acumulados disponibles',
    completeDays: 'Días con temperatura completa',
    missingDays: 'Días sin temperatura suficiente',
    complete: 'Acumulación térmica completa',
    partialNotice:
      'Acumulado parcial: faltan temperaturas en {{days}} {{dayUnit}}. No se imputan valores ni se interpretan como cero.',
    daySingular: 'día',
    dayPlural: 'días',
    stage: 'Hito observado',
    date: 'Fecha',
    status: 'Estado',
    observed: 'Observado',
    data: 'Dato',
    value: 'Valor',
    crop: 'Cultivo',
    effectiveEnd: 'Fecha final efectiva del análisis',
    closed: 'Período cerrado',
    capped: 'Límite automático aplicado',
    indicator: 'Indicador',
    unitsHeader: 'Unidad',
    climateHeaders: [
      'Fecha',
      'T mín (°C)',
      'T media (°C)',
      'T máx (°C)',
      'Precipitación (mm)',
      'ET0 (mm)',
      'GDD diario',
      'GDD acumulado',
    ],
    indicatorLabels: [
      'Precipitaciones acumuladas',
      'Evapotranspiración de referencia acumulada',
      'Balance climático indicativo',
      'Temperatura media',
      'Temperatura mínima',
      'Temperatura máxima',
      'Días con máxima ≥ 35 °C',
      'Días con mínima ≤ 0 °C',
      'Mayor período sin lluvia',
      'Completitud climática',
      'Fuente climática',
    ],
    coverage: 'Datos térmicos',
    coverageValue: '{{complete}} completos; {{missing}} sin temperatura',
    yes: 'Sí',
    no: 'No',
    unavailable: 'No disponible',
    temperatureMissing: 'Temperatura insuficiente',
    stages: {
      emergence: 'Emergencia',
      tuber_initiation: 'Inicio de tuberización',
      tuber_bulking: 'Crecimiento de tubérculos',
      senescence: 'Senescencia',
      harvest: 'Cosecha',
      adaptation: 'Adaptación',
      knee_height: 'Altura de rodilla',
      flowering_topping: 'Floración / desflore',
      harvest_start: 'Inicio de cosecha',
      harvest_end: 'Fin de cosecha',
    },
  },
  en: {
    title: 'Thermal accumulation reference',
    milestones: 'Observed field milestones',
    milestonesNotice:
      'These dates were recorded by the user; they are not predicted stages or thermal thresholds.',
    noMilestones: 'No observed milestones were recorded for this period.',
    notice:
      'Growing degree days are calculated as a reference. Stages, maturity and harvest dates are not estimated: their thresholds are not validated for this plot and material.',
    planting: 'Planting date',
    transplant: 'Transplanting date',
    end: 'Observation end',
    material: 'Field cultivar or material',
    referenceProfile: 'Published reference profile',
    potatoReferenceLabel: 'Potato - Bogotá thermal reference (2016)',
    tobaccoReferenceLabel: 'Virginia K326 tobacco - INTA Salta thermal reference (2014)',
    method: 'Calculation method',
    base: 'Base temperature (°C)',
    source: 'Reference source and scope',
    parameterOrigin: 'Parameter origin',
    publishedParameters: 'Published reference base',
    customParameters: 'User-defined base; it does not establish a stage calibration.',
    units: 'Unit',
    unitValue: '°C·day (GDD)',
    minimumMaximumMean:
      'Mean of daily minimum and maximum temperatures minus the base; non-negative accumulation.',
    dailyMean: 'Daily mean temperature minus the base; non-negative accumulation.',
    potatoScope:
      'Rodríguez et al. (2016): greenhouse potatoes in Bogotá, three Colombian cultivars. This is a thermal reference, not a calibration for this field material.',
    tobaccoScope:
      'Diez et al. (2014), INTA Salta: Virginia K326 tobacco in Chicoana, Valle de Lerma. This is a thermal reference, not a calibration for this field material.',
    accumulated: 'Available accumulated GDD',
    completeDays: 'Days with complete temperature',
    missingDays: 'Days with insufficient temperature',
    complete: 'Complete thermal accumulation',
    partialNotice:
      'Partial accumulation: temperatures are missing on {{days}} {{dayUnit}}. Values are not imputed or treated as zero.',
    daySingular: 'day',
    dayPlural: 'days',
    stage: 'Observed milestone',
    date: 'Date',
    status: 'Status',
    observed: 'Observed',
    data: 'Data',
    value: 'Value',
    crop: 'Crop',
    effectiveEnd: 'Effective analysis end date',
    closed: 'Closed period',
    capped: 'Automatic limit applied',
    indicator: 'Indicator',
    unitsHeader: 'Unit',
    climateHeaders: [
      'Date',
      'T min (°C)',
      'T mean (°C)',
      'T max (°C)',
      'Precipitation (mm)',
      'ET0 (mm)',
      'Daily GDD',
      'Accumulated GDD',
    ],
    indicatorLabels: [
      'Accumulated precipitation',
      'Accumulated reference evapotranspiration',
      'Indicative climatic balance',
      'Mean temperature',
      'Minimum temperature',
      'Maximum temperature',
      'Days with maximum ≥ 35 °C',
      'Days with minimum ≤ 0 °C',
      'Longest rain-free period',
      'Climate completeness',
      'Climate source',
    ],
    coverage: 'Thermal data',
    coverageValue: '{{complete}} complete; {{missing}} missing temperature',
    yes: 'Yes',
    no: 'No',
    unavailable: 'Unavailable',
    temperatureMissing: 'Insufficient temperature',
    stages: {
      emergence: 'Emergence',
      tuber_initiation: 'Tuber initiation',
      tuber_bulking: 'Tuber bulking',
      senescence: 'Senescence',
      harvest: 'Harvest',
      adaptation: 'Adaptation',
      knee_height: 'Knee height',
      flowering_topping: 'Flowering / topping',
      harvest_start: 'Harvest start',
      harvest_end: 'Harvest end',
    },
  },
}

const finiteNumber = (value) =>
  value === null || value === undefined || value === '' || !Number.isFinite(Number(value))
    ? null
    : Number(value)

export const getThermalReferenceExport = (analysis, locale = 'es') => {
  const agronomy = analysis?.agronomicAnalysis || {}
  const profile = agronomy.profile || analysis?.agronomicProfile || {}
  if (!isThermalOnlyAgronomicProfile(profile)) return null

  const copy = COPY[String(locale).toLowerCase().startsWith('en') ? 'en' : 'es']
  const thermalTime = profile.thermalTime || analysis?.agronomicProfile?.thermalTime || {}
  const climate = agronomy.climate || {}
  const summary = climate.summary || {}
  const phenology = agronomy.phenology || {}
  const crop = normalizeCropId(profile.crop)
  const stageIds = new Set(getAgronomicFieldObservationStageIds(profile.crop))
  const milestones = (Array.isArray(phenology.timeline) ? phenology.timeline : []).filter(
    (item) =>
      item?.basis === 'field_observation' &&
      item?.status === 'observed' &&
      stageIds.has(item.stageId)
  )

  return {
    copy,
    profile,
    milestones,
    startLabel:
      profile.startEvent === 'transplant' || crop === 'tobacco' ? copy.transplant : copy.planting,
    methodLabel:
      {
        daily_mean_linear: copy.dailyMean,
        daily_min_max_mean_linear: copy.minimumMaximumMean,
      }[thermalTime.method] || copy.unavailable,
    parameterOrigin:
      thermalTime.source === 'custom' ? copy.customParameters : copy.publishedParameters,
    baseTemperatureC: finiteNumber(thermalTime.baseTemperatureC),
    referenceId: profile.genotypeId || thermalTime.provenance?.profileId || '-',
    referenceLabel: crop === 'tobacco' ? copy.tobaccoReferenceLabel : copy.potatoReferenceLabel,
    scope: crop === 'tobacco' ? copy.tobaccoScope : copy.potatoScope,
    accumulatedGdd: finiteNumber(summary.growingDegreeDays ?? phenology.accumulatedGdd),
    completeThermalDays: finiteNumber(summary.completeThermalDays),
    missingTemperatureDays: finiteNumber(summary.missingTemperatureDays),
    thermalAccumulationComplete: summary.thermalAccumulationComplete,
    partialNotice:
      summary.thermalAccumulationComplete === false
        ? copy.partialNotice
            .replace('{{days}}', finiteNumber(summary.missingTemperatureDays) ?? copy.unavailable)
            .replace(
              '{{dayUnit}}',
              finiteNumber(summary.missingTemperatureDays) === 1 ? copy.daySingular : copy.dayPlural
            )
        : '',
  }
}

export const getThermalMilestoneCoverageLabel = (milestone, copy) =>
  copy.coverageValue
    .replace('{{complete}}', finiteNumber(milestone?.completeThermalDays) ?? copy.unavailable)
    .replace('{{missing}}', finiteNumber(milestone?.missingTemperatureDays) ?? copy.unavailable)
